import { Navigate, useParams } from 'react-router-dom';
import { Button, Card } from '@paysera/ui';
import { useTranslation } from 'react-i18next';
import { useGetPaymentScheduleQuery } from '@app/api/loanApplicationsApi';
import { PageLoader } from '@app/components/PageLoader';
import TerminateLoan from '@app/components/popups/TerminateLoan';
import { DATE_FORMAT } from '@app/configs/dateTime';
import { LoanStatuses } from '@app/configs/loanApplication';
import { ROUTES } from '@app/configs/routes';
import useBoolean from '@app/hooks/useBoolean';
import { getLocalDateTime } from '@app/utils/time';
import formatAmount from '@modules/calculator/utils/formatAmount';
import LoanStatus from '../loans/LoanStatus';
import PaymentsSchedule from './PaymentsSchedule';
const LoanDetails = () => {
    const { t } = useTranslation();
    const { id: loanId } = useParams();
    const [isLoanTerminateOpen, { setTrue: openTerminate, setFalse: closeTerminate }] = useBoolean(false);
    const { isLoading: loadingLoanApplication, data: loanApplication, error: detailsError, } = useGetPaymentScheduleQuery(loanId || '', {
        skip: !loanId,
    });
    if (loadingLoanApplication && !detailsError) {
        return <PageLoader />;
    }
    if (detailsError) {
        return <Navigate to={ROUTES.home}/>;
    }
    const isFunding = [LoanStatuses.Submitted, LoanStatuses.Collected, LoanStatuses.Collecting].includes(loanApplication === null || loanApplication === void 0 ? void 0 : loanApplication.status);
    const isActiveLoan = [LoanStatuses.Overdue, LoanStatuses.Active].includes(loanApplication === null || loanApplication === void 0 ? void 0 : loanApplication.status);
    const loanApplicationCollectedPercent = loanApplication
        ? Math.round((Number(loanApplication.collectedAmount) / Number(loanApplication.amount)) * 10000) / 100
        : 0;
    const loanIdLabel = isActiveLoan ? t('financing.loan_details.loan_id') : t('financing.loan_details.application_id');
    return (<>
            <TerminateLoan isOpen={isLoanTerminateOpen} closeModal={closeTerminate} loanId={Number(loanId)}/>

            <Card>
                <h2 className="title-l mb-6">{t('financing.loan_details.summary')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-4">
                    <div>
                        <div className="body-s text-neutral-secondary-initial">{loanIdLabel}</div>
                        <div className="label-m text-neutral-primary-initial">{loanId}</div>
                    </div>
                    <div>
                        <div className="body-s text-neutral-secondary-initial">{t('financing.active_loan.status')}</div>
                        <div className="mt-0.5">
                            {loanApplication && <LoanStatus status={loanApplication.status}/>}
                        </div>
                    </div>
                    <div>
                        <div className="body-s text-neutral-secondary-initial">
                            {t('financing.loan_details.outstanding_loan_balance')}
                        </div>
                        <div className="label-m text-neutral-primary-initial">
                            {loanApplication ? formatAmount(Number(loanApplication.outstandingBalance)) : ''}
                        </div>
                    </div>
                    {isFunding && (<div>
                            <div className="body-s text-neutral-secondary-initial">
                                {t('financing.loan_details.investment_deadline')}
                            </div>
                            <div className="label-m text-neutral-primary-initial">
                                {loanApplication
                ? getLocalDateTime(loanApplication.fundingDate, DATE_FORMAT.dateDash)
                : ''}
                            </div>
                        </div>)}
                    {isActiveLoan && (<div>
                            <div className="body-s text-neutral-secondary-initial">
                                {t('financing.loan_details.left_amount')}
                            </div>
                            <div className="label-m text-neutral-primary-initial">
                                {loanApplication ? formatAmount(Number(loanApplication.leftAmountToBeRepaid)) : ''}
                            </div>
                        </div>)}
                    <div>
                        <div className="body-s text-neutral-secondary-initial">
                            {t('financing.loan_details.loan_amount')}
                        </div>
                        <div className="label-m text-neutral-primary-initial">
                            {loanApplication ? formatAmount(Number(loanApplication.amount)) : ''}
                        </div>
                    </div>
                    {isFunding && (<div>
                            <div className="body-s text-neutral-secondary-initial">
                                {t('financing.loan_details.collected_amount')}
                            </div>
                            <div className="label-m text-neutral-primary-initial">
                                {loanApplication ? formatAmount(Number(loanApplication.collectedAmount) || 0) : ''} (
                                {loanApplicationCollectedPercent}%)
                            </div>
                        </div>)}
                    {isActiveLoan && (<div>
                            <div className="body-s text-neutral-secondary-initial">
                                {t('financing.loan_details.next_payment_date')}
                            </div>
                            <div className="label-m text-neutral-primary-initial">
                                {loanApplication
                ? getLocalDateTime(loanApplication.nextPaymentDate, DATE_FORMAT.dateDash)
                : ''}
                            </div>
                        </div>)}
                    <div>
                        <div className="body-s text-neutral-secondary-initial">
                            {t('financing.loan_details.interest')}
                        </div>
                        <div className="label-m text-neutral-primary-initial">
                            {loanApplication ? formatAmount(Number(loanApplication.interestRate)) : ''}
                        </div>
                    </div>
                    {isActiveLoan && (<div>
                            <div className="body-s text-neutral-secondary-initial">
                                {t('financing.loan_details.next_payment')}
                            </div>
                            <div className="label-m text-neutral-primary-initial">
                                {loanApplication ? formatAmount(Number(loanApplication.nextPaymentAmount)) : ''}
                            </div>
                        </div>)}
                    <div>
                        <div className="body-s text-neutral-secondary-initial">
                            {t('financing.loan_details.administration_fee')}
                        </div>
                        <div className="label-m text-neutral-primary-initial">
                            {loanApplication ? formatAmount(Number(loanApplication.administrationFee)) : ''}
                        </div>
                    </div>
                    {(loanApplication === null || loanApplication === void 0 ? void 0 : loanApplication.period) !== undefined && (<div>
                            <div className="body-s text-neutral-secondary-initial">
                                {t('financing.loan_details.period')}
                            </div>
                            <div className="label-m text-neutral-primary-initial">
                                {t('financing.loan_details.period_value', { count: loanApplication.period })}
                            </div>
                        </div>)}
                </div>
                {(loanApplication === null || loanApplication === void 0 ? void 0 : loanApplication.canBeTerminated) && (<div className="mt-6 pt-6 border-t border-neutral-primary-initial">
                        <Button variant="critical" size="md" label={t('financing.active_loan.terminate')} onClick={openTerminate}/>
                    </div>)}
            </Card>
            {!!(loanApplication === null || loanApplication === void 0 ? void 0 : loanApplication.scheduleItems.length) && (<>
                    <h1 className="my-4 md:mt-6 md:mb-4 headline-s">{t('financing.loan_details.payments_schedule')}</h1>
                    <PaymentsSchedule scheduleItems={loanApplication.scheduleItems}/>
                </>)}
        </>);
};
export default LoanDetails;
